import { TFunction } from "i18next";
import { z } from "zod";

import { FacilityType } from "../types/facility.definition";
import { CivilityEnum } from "../types/user.definition";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// MED-1458: medecine360-api validates `website` with @ValidURL, whose pattern is
//   ^$|^(https?://)?((www\.)?[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6})(/.*)?$
// (medecine360-api/src/main/java/com/ews/medecin3d/validation/ValidURL.java:12).
// The client rule has to be that exact rule: a looser one lets junk through to
// an opaque 400 on the last onboarding step (which is what "not a website"
// did), a stricter one rejects input the backend would have accepted. Note the
// 2-6 letter TLD cap — it is why `https://test-clinique-qa.example` was refused
// while `.com` was accepted.
const WEBSITE_REGEX =
  /^(https?:\/\/)?((www\.)?[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6})(\/.*)?$/;

export const facilityFormSchema = (t: TFunction) =>
  z.object({
    name: z.string().min(1, t("onboarding.facility.nameRequired")),
    type: z.enum(
      Object.values(FacilityType) as [FacilityType, ...FacilityType[]],
      {
        error: () => ({ message: t("onboarding.facility.typeRequired") }),
      }
    ),
    email: z
      .string()
      .min(1, t("onboarding.facility.emailRequired"))
      .regex(EMAIL_REGEX, t("onboarding.facility.emailInvalid")),
    phone: z.string().min(1, t("onboarding.facility.phoneRequired")),
    address: z.string().min(1, t("onboarding.facility.addressRequired")),
    city: z.string().min(1, t("onboarding.facility.cityRequired")),
    state: z.string().min(1, t("onboarding.facility.stateRequired")),
    // Senegal and Chad do not use postal codes: keep the field a string so the
    // payload still carries "" (a missing key is a 400 at the BFF), but do not require a value.
    zip: z.string(),
    accessInformation: z.string().optional(),
    photoUrl: z
      .string()
      .optional()
      .refine(
        val => {
          if (!val) return true;
          try {
            new URL(val);
            return true;
          } catch {
            return false;
          }
        },
        { message: t("onboarding.facility.photoUrlInvalid") }
      ),
    website: z.string().refine(val => !val || WEBSITE_REGEX.test(val), {
      message: t("onboarding.facility.websiteInvalid"),
    }),
  });

export type FacilityFormData = z.infer<ReturnType<typeof facilityFormSchema>>;

export const managerFormSchema = (t: TFunction) =>
  z.object({
    // MED-1543: onboarding used to post CivilityEnum.MR for every manager it
    // created, without ever asking — so half of them were addressed wrongly
    // from their very first email. The other manager forms have always asked.
    civility: z.enum(Object.values(CivilityEnum) as [string, ...string[]], {
      error: () => ({ message: t("onboarding.manager.civilityRequired") }),
    }),
    firstName: z.string().min(1, t("onboarding.manager.firstNameRequired")),
    lastName: z.string().min(1, t("onboarding.manager.lastNameRequired")),
    email: z
      .string()
      .min(1, t("onboarding.manager.emailRequired"))
      .regex(EMAIL_REGEX, t("onboarding.manager.emailInvalid")),
    phone: z.string().min(1, t("onboarding.manager.phoneRequired")),
    // Optional: a manager is an administrative account and nobody collects a
    // birth date for one, but it is shown on the detail page and the manager
    // can set it themselves from the dashboard (MED-1423).
    dateOfBirth: z.string().optional(),
  });

export type ManagerFormData = z.infer<ReturnType<typeof managerFormSchema>>;

// MED-1171: the promotion step is optional — nothing typed or picked means the
// step is simply skipped.
//
// MED-1487: the code is typed as free text, not only picked from a list, so this
// schema is also what decides whether what was typed is a promotion that exists.
// The codes are fetched asynchronously, so they are read through `getKnownCodes`
// at parse time instead of being captured when the schema is built.
//
// MED-1539: the step used to ask for a start and an end date as well. A
// promotion's validity period belongs to the promotion, not to one facility
// (licence_promotion_facility links a facility to a period and carries no dates
// of its own), so those two fields could only ever describe a *different* period
// from the one being picked — which is exactly how the facility ended up
// attached to a period nobody was looking at. The period now comes from the
// selected promotion, so the code is all that is left to validate.
export const promotionFormSchema = (
  t: TFunction,
  getKnownCodes: () => string[] = () => []
) =>
  z
    .object({
      promotionCode: z.string().optional(),
    })
    .refine(
      data => {
        const typed = data.promotionCode?.trim();
        if (!typed) return true;
        return getKnownCodes().some(
          code => code.toLowerCase() === typed.toLowerCase()
        );
      },
      {
        message: t("onboarding.promotion.codeNotFound"),
        path: ["promotionCode"],
      }
    );

export type PromotionFormData = z.infer<ReturnType<typeof promotionFormSchema>>;
